import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Compass, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { CHORD_HUB_THEME as C } from "../../../lib/chords/chordHubTheme";
import {
  CHORD_HUB_SECTION_LABELS,
  countHubCards,
  getSectionCards,
} from "../../../lib/chords/chordHubPlan";
import type { ChordHubProgress, ChordHubSection } from "../../../lib/chords/chordHubTypes";
import type { SavedProgression } from "../../../hooks/useProgressionLibrary";

export interface ChordHubLibraryProps {
  progress: ChordHubProgress;
  celebrateCardId: string | null;
  userProgressions: SavedProgression[];
  onSelectCard: (cardId: string) => void;
}

const SECTIONS: ChordHubSection[] = ["dictionary", "progressions", "gym"];

const SECTION_HERO: Record<ChordHubSection, { title: string; body: string }> = {
  dictionary: {
    title: "Biblioteca de acordes",
    body: "Triadas, tétradas e inversiones. Cada card abre un estudio focalizado con mástil a pantalla completa.",
  },
  progressions: {
    title: "Progresiones",
    body: "Presets clásicos y tus progresiones guardadas. Escucha el ciclo y sigue cada acorde en el mástil.",
  },
  gym: {
    title: "Entrenamiento",
    body: "Drills rápidos para memorizar formas. Recorre cada acorde y completa el circuito.",
  },
};

export function ChordHubLibrary({
  progress,
  celebrateCardId,
  userProgressions,
  onSelectCard,
}: ChordHubLibraryProps) {
  const [activeSection, setActiveSection] = useState<ChordHubSection>("dictionary");
  const cards = getSectionCards(activeSection, userProgressions);
  const completedCount = progress.completedCardIds.length;
  const totalCards = countHubCards(userProgressions);
  const hero = SECTION_HERO[activeSection];

  return (
    <div className="h-full min-h-0 flex flex-col font-sans overflow-hidden">
      <div
        className="shrink-0 px-4 sm:px-6 py-5 border-b"
        style={{ borderColor: C.border }}
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${C.accentSoft}, transparent)`,
                border: `1px solid ${C.border}`,
              }}
            >
              <Sparkles className="w-5 h-5" style={{ color: C.accent }} />
            </div>
            <div>
              <h1 className="font-serif text-xl font-semibold tracking-tight" style={{ color: C.textMain }}>
                Acordes
              </h1>
              <p className="text-xs" style={{ color: C.textDim }}>
                {completedCount}/{totalCards} lecciones · mapa de aprendizaje
              </p>
            </div>
          </div>

          <Link
            to="/chords/explorer"
            className="inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl transition-colors hover:bg-white/5"
            style={{ color: C.textMuted, border: `1px solid ${C.border}` }}
          >
            <Compass className="w-3.5 h-3.5" style={{ color: C.accent }} />
            Explorador libre
          </Link>
        </div>
      </div>

      <div className="shrink-0 px-4 sm:px-6 pt-4 max-w-5xl mx-auto w-full">
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => setActiveSection(section)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{
                backgroundColor: activeSection === section ? C.accentSoft : "rgba(255,255,255,0.04)",
                color: activeSection === section ? C.accent : C.textDim,
                border: `1px solid ${activeSection === section ? `${C.accent}44` : C.border}`,
              }}
            >
              {CHORD_HUB_SECTION_LABELS[section]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <HubHero title={hero.title} body={hero.body} />

          {cards.length === 0 && (
            <p className="text-sm mt-4 text-center" style={{ color: C.textDim }}>
              No hay lecciones en esta sección todavía.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {cards.map((card) => (
              <HubCard
                key={card.id}
                title={card.title}
                subtitle={card.subtitle}
                tags={card.tags}
                timeMin={card.timeEstimateMin}
                completed={progress.completedCardIds.includes(card.id)}
                celebrating={celebrateCardId === card.id}
                onOpen={() => onSelectCard(card.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HubHero({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        backgroundColor: C.bgCard,
        border: `1px solid ${C.border}`,
        backgroundImage: `linear-gradient(135deg, ${C.accentSoft}, transparent 55%)`,
      }}
    >
      <h2 className="text-base font-bold mb-1" style={{ color: C.textMain }}>
        {title}
      </h2>
      <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>
        {body}
      </p>
    </div>
  );
}

function HubCard({
  title,
  subtitle,
  tags,
  timeMin,
  completed,
  celebrating,
  onOpen,
}: {
  title: string;
  subtitle: string;
  tags: string[];
  timeMin: number;
  completed: boolean;
  celebrating: boolean;
  onOpen: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      onClick={onOpen}
      animate={
        celebrating
          ? { scale: [1, 1.03, 1], boxShadow: [`0 0 0 0 ${C.accentGlow}`, `0 0 24px ${C.accentGlow}`, `0 0 0 0 transparent`] }
          : { scale: 1 }
      }
      transition={{ duration: 0.55, ease: "easeOut" }}
      className="text-left rounded-xl overflow-hidden transition-colors hover:brightness-110"
      style={{
        backgroundColor: C.bgCard,
        border: `1px solid ${celebrating ? C.accent : completed ? "rgba(34,197,94,0.35)" : C.border}`,
      }}
    >
      <div
        className="h-[2px] w-full"
        style={{
          background: completed
            ? "linear-gradient(90deg, #34d399, transparent)"
            : `linear-gradient(90deg, ${C.accent}, transparent)`,
        }}
      />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
            style={{ backgroundColor: C.accentSoft, color: C.accent }}
          >
            ♪
          </span>
          {completed && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
              <Check className="w-3 h-3" />
              Completado
            </span>
          )}
        </div>
        <h3 className="font-semibold text-sm mb-0.5 line-clamp-2" style={{ color: C.textMain }}>
          {title}
        </h3>
        <p className="text-xs line-clamp-2 mb-3" style={{ color: C.textMuted }}>
          {subtitle}
        </p>
        <div className="flex flex-wrap gap-1">
          {tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: C.bgElevated, color: C.textDim }}
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="text-[10px] mt-3" style={{ color: C.textDim }}>
          ~{timeMin} min
        </p>
      </div>
    </motion.button>
  );
}
